import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from 'hooks';
import { useApiCall } from 'hooks/useApiCall';
import { ExecutorsBodyTemplate } from 'pages/tasks/columnsTemplates';
import { selectAllId, WITHOUT_DEPARTMENTS_ID } from 'pages/tasks/constants';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { useDebounce as debouncedState } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import {
  TriStateCheckbox,
  TriStateCheckboxChangeEvent
} from 'primereact/tristatecheckbox';
import { classNames } from 'primereact/utils';
import { selectedDate, TreeNode } from 'types/interfaces';
import { clearFilter, findNodeByKey, findNodesByKey, getAllKeys } from 'utils';

import { DepartmentsService } from 'components/services';
import { PersonAssignmentHistoryService } from 'components/services/PersonAssignmentHistoryService';
import { CustomDropdown } from 'ui/custom-controls';

import { DROPDOWN_FILTER_DELAY } from '../../../constants';
import {
  EMPLOYEE_ASSIGNMENT_HISTORY_ERROR_LABELS,
  formatDepartmentAssignmentErrors,
  formatDepartmentAssignmentHistoryType
} from '../../departmentAssignmentHistory/model/departmentAssignmentHistory';
import { personAssignmentHistoryColumnWidths as colW } from '../config/columnWidths';
import { personAssignmentHistoryErrorFilterOptions } from '../config/personAssignmentHistoryErrorFilterOptions';
import {
  type PersonAssignmentHistoryFilterOption,
  personAssignmentHistoryTypeFilterOptions
} from '../config/typeFilterOptions';
import type { PersonAssignmentHistoryItem } from '../model/types';

import PersonAssignmentHistoryHeader from './PersonAssignmentHistoryHeader';


/**
 * TODO(refactor-history-tables): почти дублирует DepartmentAssignmentHistory — вынести в
 * useAssignmentHistoryTable(options) или общий layout + конфиг колонок.
 * Общее: loadData, JSON.stringify фильтров, dateBetween, debounce, onFiltersChange, scrollHeight,
 * пагинация, isValidation, enum type/errors + CustomDropdown, formatDepartmentAssignment*, clearFilter.
 * Различия: поля строки, сервис API, набор колонок. См. DepartmentAssignmentHistory.tsx.
 */

export default function PersonAssignmentHistory() {
  const [items, setItems] = useState<PersonAssignmentHistoryItem[]>([]);
  const [treeDropDownValue, setTreeDropDownValue] = useState<TreeNode[]>([]);
  const [isDepartmentsLoaded, setIsDepartmentsLoaded] = useState(false);
  const [departments, setDepartments] = useState<TreeNode[]>([]);
  const [pageSize, setPageSize] = useState(100);
  const isFirstRender = useRef(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [
    selectedDepartmentsFilter,
    dbouncedSelectedDepartmentsFilter,
    setSelectedDepartmentsFilter
  ] = debouncedState<TreeNode[]>([], 700);
  const [selectedDateFilter, setSelectedDateFilter] = useState<selectedDate>({
    dateFrom: '',
    dateUntil: ''
  });
  const toast = useRef<Toast>(null);
  const [validationFilter, setValidationFilter] = useState<boolean | null>(
    null
  );

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    asset: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    employees: { value: null, matchMode: FilterMatchMode.CONTAINS },
    user: { value: null, matchMode: FilterMatchMode.CONTAINS },
    departmentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    errors: { value: null, matchMode: FilterMatchMode.EQUALS }
  });

  const idFilterRef = useRef<HTMLInputElement>(null);
  const taskNameFilterRef = useRef<HTMLInputElement>(null);
  const taskTypeFilterRef = useRef<HTMLInputElement>(null);
  const assetFilterRef = useRef<HTMLInputElement>(null);
  const timeFilterRef = useRef<HTMLInputElement>(null);
  const employeesFilterRef = useRef<HTMLInputElement>(null);
  const authorFilterRef = useRef<HTMLInputElement>(null);
  const departmentFilterRef = useRef<HTMLInputElement>(null);

  const tableElement = document.querySelector('.p-datatable-thead');

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  function offset(el: Element) {
    const rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }
  
  const tableOffset = useMemo(() => {
    return tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
  }, [items, dbouncedSelectedDepartmentsFilter]);

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement, tableOffset]);

  const getValidTimestamp = (value: string | undefined) => {
    if (!value) return '';

    if (
      typeof value === 'string' &&
      (value.includes('T') || value.includes('Z'))
    ) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString();
    }

    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1577836800000) {
      return '';
    }
    return new Date(num).toISOString();
  };

  const departmentValue = useMemo(() => {
    const isSelectAllSelected =
      findNodeByKey(dbouncedSelectedDepartmentsFilter, selectAllId) !== null;
    const isWithoutDepartmentsSelected =
      findNodeByKey(dbouncedSelectedDepartmentsFilter, WITHOUT_DEPARTMENTS_ID) !==
      null;

    if (dbouncedSelectedDepartmentsFilter.length === 0) {
      return 'empty';
    }
    if (isSelectAllSelected) {
      return isWithoutDepartmentsSelected
        ? ['', ...getAllKeys(departments)].join(',')
        : getAllKeys(departments).join(',');
    }
    return dbouncedSelectedDepartmentsFilter
      .map((x) => (x.key === WITHOUT_DEPARTMENTS_ID ? '' : x.key ?? ''))
      .join(',');
  }, [dbouncedSelectedDepartmentsFilter, departments]);

  useEffect(() => {
    if (!isDepartmentsLoaded) return;

    const params = new URLSearchParams();

    if (selectedDepartmentsFilter.length > 0) {
      const deptIds = selectedDepartmentsFilter
        .filter((dept: { key: string }) => dept.key !== selectAllId)
        .map((dept: { key: any }) => dept.key)
        .join(',');
      if (deptIds) params.set('departmentIds', deptIds);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [isDepartmentsLoaded, selectedDepartmentsFilter, selectedDateFilter]);

  const loadData = useCallback(() => {
    if (!isDepartmentsLoaded || dbouncedSelectedDepartmentsFilter.length < 1) {
      setItems([]);
      setTotalCount(0);
      return;
    }

    const sort = multiSortMeta?.[0];
    const mergedFilters: Record<string, DataTableFilterMetaData> = {
      ...filters,
      isValidation: {
        value: validationFilter,
        matchMode: FilterMatchMode.EQUALS
      }
    };
    const baseFilters: {
      field: string;
      value: unknown;
      operator?: string;
      additionalValue?: string;
    }[] = Object.keys(mergedFilters).map((key) => {
      const f = mergedFilters[key];
      return {
        field: key,
        value: f?.value,
        operator: f?.matchMode
      };
    });
    baseFilters.push({
      field: 'department',
      value: departmentValue,
      operator: FilterMatchMode.IN
    });
    baseFilters.push({
      field: 'dateBetween',
      value: getValidTimestamp(selectedDateFilter.dateFrom),
      additionalValue: getValidTimestamp(selectedDateFilter.dateUntil),
      operator: FilterMatchMode.BETWEEN
    });
    const payload = JSON.stringify(baseFilters);

    PersonAssignmentHistoryService.getHistory({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    })
      .then((res) => {
        if (res.data?.isSuccess) {
          const list = (res.data.value?.itemsList ||
            []) as PersonAssignmentHistoryItem[];
          setItems(list.map((item) => ({
            ...item, departmentName: item.department
          })));
          setTotalCount(res.data.value?.total ?? 0);
        } else {
          setItems([]);
          setTotalCount(0);
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Не удалось загрузить историю назначений по сотрудникам',
            life: 5000
          });
        }
      })
      .catch((error) => {
        console.error('PersonAssignmentHistory load error:', error);
        setItems([]);
        setTotalCount(0);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить историю назначений по сотрудникам',
          life: 5000
        });
      });
  }, [
    filters,
    multiSortMeta,
    pageNumber,
    pageSize,
    selectedDateFilter,
    validationFilter,
    departmentValue,
    isDepartmentsLoaded,
    dbouncedSelectedDepartmentsFilter
  ]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    if (!isDepartmentsLoaded || dbouncedSelectedDepartmentsFilter.length < 1) {
      setItems([]);
      setTotalCount(0);
      return;
    }
    debouncedLoadData();
  }, [
    debouncedLoadData,
    filters,
    multiSortMeta,
    pageNumber,
    pageSize,
    selectedDateFilter,
    validationFilter,
    departmentValue,
    dbouncedSelectedDepartmentsFilter,
    isDepartmentsLoaded
  ]);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    const { isValidation: _omitValidation, ...nextWithoutValidation } = next;
    setFilters((prev) => ({
      ...prev,
      ...nextWithoutValidation,
      global: next.global ?? prev.global,
      type: next.type ?? prev.type,
      errors: next.errors ?? prev.errors
    }));
  }, []);

  const bumpPageToFirst = useCallback(() => {
    setPageNumber(1);
    setFirst(0);
  }, []);

  const onFilterChange = useCallback(
    (filter: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters((prev) => {
        const _filters = JSON.parse(JSON.stringify(prev));
        if (!_filters[filter]) return prev;
        _filters[filter].value = value || null;
        return _filters;
      });
      bumpPageToFirst();
    },
    [bumpPageToFirst]
  );

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleFilterChange = useCallback(
    (filter: string, value: string) => {
      if (filter !== 'global') return;
      const mockEvent = {
        target: { value }
      } as React.ChangeEvent<HTMLInputElement>;
      debouncedOnFilterChange('global', mockEvent);
    },
    [debouncedOnFilterChange]
  );

  const handleSelectionChange = useCallback(
    (selection: TreeNode[] | null) => {
      if (!isDepartmentsLoaded) return;

      bumpPageToFirst();
      setSelectedDepartmentsFilter(selection ?? []);

      if (!selection?.length) {
        toast.current?.show({
          severity: 'warn',
          detail: 'Фильтр участков сброшен'
        });
      }
    },
    [isDepartmentsLoaded, bumpPageToFirst, setSelectedDepartmentsFilter]
  );

  const { debouncedCallback: debouncedHandleSelectionChange } = useDebounce(
    handleSelectionChange,
    DROPDOWN_FILTER_DELAY
  );

  const handleDeleteTagItem = useCallback(
    (taskId: string | number) => {
      setSelectedDepartmentsFilter((current) =>
        current.filter((node) => node.key !== String(taskId))
      );
    },
    [setSelectedDepartmentsFilter]
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const taskIdBodyTemplate = (rowData: PersonAssignmentHistoryItem) => {
    return (
      <span
        style={{ cursor: 'pointer', color: '#007ad9' }}
        onClick={() => {
          window.open(`/${rowData.taskId}/?tab=history`, '_blank');
        }}
      >
        {rowData.taskId}
      </span>
    );
  };

  const dateBodyTemplate = (rowData: PersonAssignmentHistoryItem) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      {rowData.dateFormatted ?? rowData.date ?? ''}
    </span>
  );

  const typeBodyTemplate = (rowData: PersonAssignmentHistoryItem) =>
    formatDepartmentAssignmentHistoryType(rowData.type);

  const errorsBodyTemplate = (rowData: PersonAssignmentHistoryItem) =>
    formatDepartmentAssignmentErrors(rowData.errors, EMPLOYEE_ASSIGNMENT_HISTORY_ERROR_LABELS);

  const validationBodyTemplate = (rowData: PersonAssignmentHistoryItem) => (
    <i
      className={classNames('pi', {
        'true-icon pi-check-circle': rowData.isValidation,
        'false-icon pi-times-circle': !rowData.isValidation
      })}
    />
  );

  const selectedTypeFilterOption =
    useMemo<PersonAssignmentHistoryFilterOption | null>(() => {
      const currentValue = filters.type?.value;
      if (currentValue === null || currentValue === undefined) {
        return null;
      }
      return (
        personAssignmentHistoryTypeFilterOptions.find(
          (option) => option.value === currentValue
        ) ?? null
      );
    }, [filters.type?.value]);

  const selectedErrorsFilterOption =
    useMemo<PersonAssignmentHistoryFilterOption | null>(() => {
      const currentValue = filters.errors?.value;
      if (currentValue === null || currentValue === undefined) {
        return null;
      }
      return (
        personAssignmentHistoryErrorFilterOptions(EMPLOYEE_ASSIGNMENT_HISTORY_ERROR_LABELS).find(
          (option) => option.value === currentValue
        ) ?? null
      );
    }, [filters.errors?.value]);

  const handleDateFilterChange = useCallback((selection: selectedDate) => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const isDateFromValid =
      selection.dateFrom !== null &&
      !isNaN(new Date(selection.dateFrom)?.getTime());
    const isDateUntilValid =
      selection.dateUntil !== null &&
      !isNaN(new Date(selection.dateUntil)?.getTime());
    if (isDateFromValid || isDateUntilValid) {
      const newDateFrom = isDateFromValid
        ? String(new Date(selection.dateFrom!)?.getTime())
        : '';
      const newDateUntil = isDateUntilValid
        ? String(new Date(selection.dateUntil!)?.getTime())
        : '';

      if (
        newDateFrom !== selectedDateFilter.dateFrom ||
        newDateUntil !== selectedDateFilter.dateUntil
      ) {
        setSelectedDateFilter({
          dateFrom: newDateFrom,
          dateUntil: newDateUntil
        });
        setPageNumber(1);
        setFirst(0);
      }
    } else {
      setSelectedDateFilter({ dateFrom: '', dateUntil: '' });
      setPageNumber(1);
      setFirst(0);
    }
  }, []);

  const getDepartmentsCallback = useCallback(async () => {
    try {
      setIsDepartmentsLoaded(false);
      const result = await DepartmentsService.getDepartments();
      if (!result.data || !Array.isArray(result.data)) {
        console.error('Invalid departments data');
        setIsDepartmentsLoaded(true);
        return;
      }
      setDepartments(result.data ? [...result.data] : []);
      setTreeDropDownValue(
        result.data
          ? [
            {
              key: selectAllId,
              label: 'Выбрать все участки'
            },
            ...result.data
          ]
          : [
            { key: selectAllId, label: 'Выбрать все участки' },
          ]
      );

      const departmentIds = new URLSearchParams(window.location.search)
        .get('departmentIds')
        ?.split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (departmentIds?.length) {
        const selectedDepartments = findNodesByKey(
          result.data,
          departmentIds.filter((id) => id !== selectAllId)
        );

        if (departmentIds.includes(selectAllId)) {
          selectedDepartments.unshift({
            key: selectAllId,
            label: 'Выбрать все участки'
          });
        }

        setSelectedDepartmentsFilter(selectedDepartments);
      }
    } catch (error) {
      console.error('Ошибка загрузки участков:', error);
      toast.current?.show({
        severity: 'error',
        detail: 'Не удалось загрузить участки'
      });
    } finally {
      setIsDepartmentsLoaded(true);
    }
  }, [setSelectedDepartmentsFilter]);

  useApiCall(getDepartmentsCallback);

  const header = (
    <PersonAssignmentHistoryHeader
      handleFilterChange={handleFilterChange}
      selectedDepartmentsFilter={selectedDepartmentsFilter.map(
        (department) =>
          department.label === 'Выбрать все участки'
            ? { ...department, label: 'Выбраны все участки' }
            : department
      )}
      treeDropDownValue={treeDropDownValue}
      handleSelectionChange={debouncedHandleSelectionChange}
      handleDateFilterChange={handleDateFilterChange}
      datepickerDate={selectedDateFilter}
      handleDeleteTagItem={handleDeleteTagItem}
      isDepartmentsLoaded={isDepartmentsLoaded}
    />
  );


  return (
    <div className="personAssignmentHistory-table wrapper wrapper--main">
      <Toast ref={toast} />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${items.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        value={items}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Записей не найдено."
        tableStyle={{ minWidth: '100%', width: '100%' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="taskId"
          filter
          sortable
          field="taskId"
          header="№ наряда"
          style={{ width: colW.taskId }}
          headerStyle={{ width: colW.taskId }}
          onFilterClear={() =>
            clearFilter(
              'taskId',
              filters,
              setFilters,
              idFilterRef,
              setPageNumber,
              setFirst
            )
          }
          body={taskIdBodyTemplate}
          filterElement={
            <InputText
              ref={idFilterRef}
              className="w-full"
              onChange={handleInputChange('taskId')}
              placeholder="Поиск по № наряда"
            />
          }
        />
        <Column
          columnKey="taskName"
          filter
          sortable
          field="taskName"
          header="Название наряда"
          style={{ width: colW.taskName }}
          headerStyle={{ width: colW.taskName }}
          onFilterClear={() =>
            clearFilter(
              'taskName',
              filters,
              setFilters,
              taskNameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={taskNameFilterRef}
              className="w-full"
              onChange={handleInputChange('taskName')}
              placeholder="Поиск по названию"
            />
          }
        />
        <Column
          columnKey="type"
          filter
          sortable
          field="type"
          header={
            <span className="flex align-items-center gap-2">
              <span>Статус распределения</span>
              <i
                className="pi pi-info-circle person-assignment-status-filter-hint"
                style={{ color: '#3b82f6', cursor: 'pointer' }}
                data-pr-tooltip='Глобальный поиск не работает по полю "Статус распределения".'
              />
            </span>
          }
          style={{ width: colW.type }}
          headerStyle={{ width: colW.type }}
          body={typeBodyTemplate}
          onFilterClear={() => {
            setFilters((prev) => ({
              ...prev,
              type: { ...prev.type, value: null, matchMode: FilterMatchMode.EQUALS }
            }));
            setPageNumber(1);
            setFirst(0);
          }}
          filterElement={
            <div className="w-full">
              <CustomDropdown<PersonAssignmentHistoryFilterOption>
                style={{ maxWidth: '125px', minWidth: 0 }}
                assetOptions={personAssignmentHistoryTypeFilterOptions}
                selectedAsset={selectedTypeFilterOption}
                fieldName="label"
                placeholder="Все"
                showClearOnHover
                onSelectionChange={(selected) => {
                  const selectedOption =
                    (selected as PersonAssignmentHistoryFilterOption | null) ?? null;
                  setFilters((prev) => ({
                    ...prev,
                    type: {
                      ...prev.type,
                      value: selectedOption?.value ?? null,
                      matchMode: FilterMatchMode.EQUALS
                    }
                  }));
                  bumpPageToFirst();
                }}
              />
            </div>
          }
        />
        <Column
          columnKey="taskType"
          filter
          sortable
          field="taskType"
          header="Тип наряда"
          style={{ width: colW.taskType }}
          headerStyle={{ width: colW.taskType }}
          onFilterClear={() =>
            clearFilter(
              'taskType',
              filters,
              setFilters,
              taskTypeFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={taskTypeFilterRef}
              className="w-full"
              onChange={handleInputChange('taskType')}
              placeholder="Поиск по типу"
            />
          }
        />
        <Column
          columnKey="asset"
          filter
          sortable
          field="asset"
          header="Объект"
          style={{ width: colW.asset }}
          headerStyle={{ width: colW.asset }}
          onFilterClear={() =>
            clearFilter('asset', filters, setFilters, assetFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={assetFilterRef}
              className="w-full"
              onChange={handleInputChange('asset')}
              placeholder="Поиск по объекту"
            />
          }
        />
        <Column
          columnKey="date"
          filter
          sortable
          field="date"
          filterField="date"
          sortField="date"
          header="Дата и время распределения"
          style={{ width: colW.date }}
          headerStyle={{
            width: colW.date,
            whiteSpace: 'normal',
            textAlign: 'center',
            lineHeight: 1.25
          }}
          body={dateBodyTemplate}
          onFilterClear={() =>
            clearFilter('date', filters, setFilters, timeFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={timeFilterRef}
              className="w-full"
              onChange={handleInputChange('date')}
              placeholder="Поиск по дате"
            />
          }
        />
        <Column
          key="employees"
          columnKey="employees"
          sortable
          sortField="employees"
          field="employees"
          header="Сотрудники"
          body={(data) => (<ExecutorsBodyTemplate employees={data.employees} />)}
          headerStyle={{
            width: colW.employees,
            whiteSpace: 'normal',
            textAlign: 'center',
            lineHeight: 1.25
          }}
          filterField="employees"
          filter
          showAddButton={false}
          filterElement={
            <InputText
              ref={employeesFilterRef}
              className="w-full"
              onChange={handleInputChange('employees')}
              placeholder="Поиск по сотрудникам"
            />
          }
          onFilterClear={() =>
            clearFilter('employees', filters, setFilters, employeesFilterRef, setPageNumber, setFirst)
          }
        />
        <Column
          columnKey="user"
          filter
          sortable
          field="user"
          header="Кем назначен"
          style={{ width: colW.user }}
          headerStyle={{ width: colW.user }}
          onFilterClear={() =>
            clearFilter('user', filters, setFilters, authorFilterRef, setPageNumber, setFirst)
          }
          filterElement={
            <InputText
              ref={authorFilterRef}
              className="w-full"
              onChange={handleInputChange('user')}
              placeholder="Поиск по пользователю"
            />
          }
        />
        <Column
          columnKey="departmentName"
          filter
          sortable
          field="departmentName"
          header="Участок"
          style={{ width: colW.department }}
          headerStyle={{ width: colW.department }}
          onFilterClear={() =>
            clearFilter(
              'departmentName',
              filters,
              setFilters,
              departmentFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={departmentFilterRef}
              className="w-full"
              onChange={handleInputChange('departmentName')}
              placeholder="Поиск по участку"
            />
          }
        />
        <Column
          columnKey="isValidation"
          filter
          sortable
          field="isValidation"
          header="Валидация"
          dataType="boolean"
          style={{ width: colW.isValidation }}
          headerStyle={{ width: colW.isValidation }}
          body={validationBodyTemplate}
          onFilterClear={() => {
            setValidationFilter(null);
            setPageNumber(1);
            setFirst(0);
          }}
          filterElement={
            <TriStateCheckbox
              id="personAssignmentHistoryValidationFilter"
              value={validationFilter}
              onChange={(e: TriStateCheckboxChangeEvent) => {
                setValidationFilter(e.value ?? null);
                bumpPageToFirst();
              }}
            />
          }
        />
        <Column
          columnKey="errors"
          filter
          sortable
          field="errors"
          header={
            <span className="flex align-items-center gap-2">
              <span>Примечание</span>
              <i
                className="pi pi-info-circle person-assignment-errors-filter-hint"
                style={{ color: '#3b82f6', cursor: 'pointer' }}
                data-pr-tooltip='Глобальный поиск не работает по полю "Примечание".'
              />
            </span>
          }
          style={{ width: colW.errors }}
          headerStyle={{ width: colW.errors }}
          body={errorsBodyTemplate}
          onFilterClear={() => {
            setFilters((prev) => ({
              ...prev,
              errors: { ...prev.errors, value: null, matchMode: FilterMatchMode.EQUALS }
            }));
            bumpPageToFirst();
          }}
          filterElement={
            <div className="w-full">
              <CustomDropdown<PersonAssignmentHistoryFilterOption>
                assetOptions={personAssignmentHistoryErrorFilterOptions(EMPLOYEE_ASSIGNMENT_HISTORY_ERROR_LABELS)}
                selectedAsset={selectedErrorsFilterOption}
                style={{ maxWidth: '192px', minWidth: 0 }}
                fieldName="label"
                placeholder="Все"
                showClearOnHover
                onSelectionChange={(selected) => {
                  const selectedOption =
                    (selected as PersonAssignmentHistoryFilterOption | null) ?? null;
                  setFilters((prev) => ({
                    ...prev,
                    errors: {
                      ...prev.errors,
                      value: selectedOption?.value ?? null,
                      matchMode: FilterMatchMode.EQUALS
                    }
                  }));
                  bumpPageToFirst();
                }}
              />
            </div>
          }
        />
      </DataTable>
      <Tooltip target=".person-assignment-status-filter-hint" position="top" />
      <Tooltip target=".person-assignment-errors-filter-hint" position="top" />
    </div>
  );
}
